/**
 * HYLA EST Scroll by Notch
 * Sticky canvas scrub through 49 WebP frames. No GSAP.
 */
(() => {
  const TAG = 'hyla-est-scroll-notch';
  const FRAME_COUNT = 49;
  const MAX_CACHE = 12;
  const LOAD_CONCURRENCY = 3;
  const SOURCE_W = 1890;
  const SOURCE_H = 2160;

  class HylaEstScrollNotch extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._alive = true;
      this._raf = 0;
      this._desiredFrame = 0;
      this._drawnFrame = -1;
      this._cache = new Map(); // index -> ImageBitmap | HTMLImageElement
      this._loading = new Set();
      this._queue = [];
      this._activeLoads = 0;
      this._ready = false;
      this._staticMode = false;

      this.pin = this.querySelector('[data-hesn-pin]');
      this.stage = this.querySelector('[data-hesn-stage]');
      this.canvas = this.querySelector('[data-hesn-canvas]');
      this.poster = this.querySelector('[data-hesn-poster]');
      this.copies = this.querySelectorAll('[data-hesn-copy]');
      this.ctx = this.canvas ? this.canvas.getContext('2d', { alpha: true }) : null;

      this.frameUrls = this._readManifest();
      this.maxDpr = Math.min(3, Math.max(1, Number(this.dataset.maxDpr) || 2));
      this.staticFrame = this._clampFrame(Number(this.dataset.staticFrame) || 0);
      this.mobileAnimation = String(this.dataset.mobileAnimation) !== 'false';
      this.textFade = String(this.dataset.textFade) !== 'false';

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._tick = this._tick.bind(this);

      this._initMode();
      this._bindLifecycle();
      this._setupObservers();
      this._resizeCanvas();
      this._bootstrapFrames();
      this._setupTextReveal();
      this._schedule();
    }

    disconnectedCallback() {
      this._alive = false;
      this._connected = false;
      if (this._abort) this._abort.abort();
      if (this._raf) cancelAnimationFrame(this._raf);
      if (this._io) this._io.disconnect();
      if (this._textIo) this._textIo.disconnect();
      if (this._ro) this._ro.disconnect();
      this._releaseCache();
    }

    _signal() {
      return this._abort ? this._abort.signal : undefined;
    }

    _readManifest() {
      const node = this.querySelector('[data-hyla-est-scroll-frames]');
      if (!node) return [];
      try {
        const data = JSON.parse(node.textContent.trim());
        return Array.isArray(data) ? data.filter((u) => typeof u === 'string' && u.length) : [];
      } catch (e) {
        return [];
      }
    }

    _clampFrame(n) {
      if (!Number.isFinite(n)) return 0;
      return Math.max(0, Math.min(FRAME_COUNT - 1, Math.round(n)));
    }

    _prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _isMobile() {
      return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    }

    _initMode() {
      const reduced = this._prefersReducedMotion();
      const mobileStatic = this._isMobile() && !this.mobileAnimation;
      this._staticMode = reduced || mobileStatic || this.frameUrls.length === 0;
      this.classList.toggle('is-static', this._staticMode);
      if (this._staticMode) {
        this._desiredFrame = this.staticFrame;
        if (this.poster && this.frameUrls[this.staticFrame]) {
          this.poster.src = this.frameUrls[this.staticFrame];
        }
      }
    }

    _bindLifecycle() {
      const signal = this._signal();
      if (!this._staticMode) {
        window.addEventListener('scroll', this._onScroll, { passive: true, signal });
      }
      window.addEventListener('resize', this._onResize, { passive: true, signal });
      window.addEventListener('orientationchange', this._onResize, { passive: true, signal });
    }

    _setupObservers() {
      if ('IntersectionObserver' in window) {
        this._inView = true;
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this._inView = entry.isIntersecting;
              if (this._inView) this._schedule();
            });
          },
          { rootMargin: '200px 0px' }
        );
        this._io.observe(this);
      } else {
        this._inView = true;
      }

      if ('ResizeObserver' in window && this.stage) {
        this._ro = new ResizeObserver(() => this._onResize());
        this._ro.observe(this.stage);
      }
    }

    _setupTextReveal() {
      if (!this.textFade || this._staticMode || this._prefersReducedMotion()) {
        this.copies.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      if (!('IntersectionObserver' in window)) {
        this.copies.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      this._textIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this._textIo.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.01,
          // Reveal near lower ~80–85% of viewport (not while still far below)
          rootMargin: '0px 0px -15% 0px',
        }
      );
      this.copies.forEach((el) => this._textIo.observe(el));
    }

    _onScroll() {
      if (!this._alive || this._staticMode || !this._inView) return;
      this._schedule();
    }

    _onResize() {
      if (!this._alive) return;
      const wasStatic = this._staticMode;
      this._initMode();
      if (wasStatic !== this._staticMode) {
        // Mode flipped — rebind scroll if needed
        if (!this._staticMode) {
          window.addEventListener('scroll', this._onScroll, { passive: true, signal: this._signal() });
        }
      }
      this._resizeCanvas();
      this._drawnFrame = -1;
      this._schedule();
    }

    _schedule() {
      if (this._raf || !this._alive) return;
      this._raf = requestAnimationFrame(this._tick);
    }

    _tick() {
      this._raf = 0;
      if (!this._alive) return;
      this._updateProgress();
      this._ensureFrame(this._desiredFrame);
      this._draw();
      this._prioritizeLoads(this._desiredFrame);
    }

    _updateProgress() {
      if (this._staticMode) {
        this._desiredFrame = this.staticFrame;
        return;
      }
      if (!this.pin) return;

      const rect = this.pin.getBoundingClientRect();
      const pinHeight = this.pin.offsetHeight;
      const stageHeight = this.stage ? this.stage.offsetHeight : window.innerHeight;
      const scrollable = Math.max(1, pinHeight - stageHeight);
      // When pin top hits viewport top, progress starts; completes when bottom reaches viewport bottom of stage
      const scrolled = Math.min(scrollable, Math.max(0, -rect.top));
      const progress = scrolled / scrollable;
      this._desiredFrame = this._clampFrame(progress * (FRAME_COUNT - 1));
    }

    _resizeCanvas() {
      if (!this.canvas || !this.ctx || !this.stage) return;
      const product = this.querySelector('[data-hesn-product]');
      const cssW = product ? product.clientWidth : this.stage.clientWidth;
      const cssH = product ? product.clientHeight : this.stage.clientHeight;
      if (cssW < 2 || cssH < 2) return;
      const dpr = Math.min(this.maxDpr, window.devicePixelRatio || 1);
      const w = Math.round(cssW * dpr);
      const h = Math.round(cssH * dpr);
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
        this._drawnFrame = -1;
      }
    }

    async _bootstrapFrames() {
      if (!this.frameUrls.length) return;
      const first = this._staticMode ? this.staticFrame : 0;
      await this._loadFrame(first);
      if (!this._alive) return;
      this._draw();
      // Progressive preload remaining in order, but bounded concurrency
      for (let i = 0; i < this.frameUrls.length; i += 1) {
        if (i === first) continue;
        this._queue.push(i);
      }
      this._pumpQueue();
    }

    _prioritizeLoads(center) {
      // Move nearby unloaded frames to front of queue
      const nearby = [];
      for (let d = 1; d <= 4; d += 1) {
        const a = center - d;
        const b = center + d;
        if (a >= 0) nearby.push(a);
        if (b < FRAME_COUNT) nearby.push(b);
      }
      nearby.reverse().forEach((idx) => {
        if (this._cache.has(idx) || this._loading.has(idx)) return;
        const pos = this._queue.indexOf(idx);
        if (pos > 0) this._queue.splice(pos, 1);
        if (pos !== 0) this._queue.unshift(idx);
      });
      this._pumpQueue();
    }

    _pumpQueue() {
      while (this._activeLoads < LOAD_CONCURRENCY && this._queue.length) {
        const idx = this._queue.shift();
        if (this._cache.has(idx) || this._loading.has(idx)) continue;
        this._loadFrame(idx);
      }
    }

    _loadFrame(index) {
      if (!this._alive) return Promise.resolve(null);
      if (this._cache.has(index)) return Promise.resolve(this._cache.get(index));
      if (this._loading.has(index)) return Promise.resolve(null);
      const url = this.frameUrls[index];
      if (!url) return Promise.resolve(null);

      this._loading.add(index);
      this._activeLoads += 1;

      return this._fetchBitmap(url)
        .then((bitmap) => {
          if (!this._alive) {
            this._closeBitmap(bitmap);
            return null;
          }
          this._cacheSet(index, bitmap);
          if (index === this._desiredFrame || !this._ready) {
            this._drawnFrame = -1;
            this._schedule();
          }
          return bitmap;
        })
        .catch(() => null)
        .finally(() => {
          this._loading.delete(index);
          this._activeLoads = Math.max(0, this._activeLoads - 1);
          this._pumpQueue();
        });
    }

    _fetchBitmap(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = async () => {
          try {
            if (typeof createImageBitmap === 'function') {
              const bmp = await createImageBitmap(img);
              resolve(bmp);
            } else {
              resolve(img);
            }
          } catch (e) {
            resolve(img);
          }
        };
        img.onerror = reject;
        img.src = url;
      });
    }

    _cacheSet(index, value) {
      if (this._cache.has(index)) {
        this._cache.delete(index);
      }
      this._cache.set(index, value);
      while (this._cache.size > MAX_CACHE) {
        // Evict farthest from desired frame (LRU-ish: first key not near desired)
        let evictKey = null;
        let farthest = -1;
        this._cache.forEach((_v, key) => {
          const dist = Math.abs(key - this._desiredFrame);
          // Never evict current or static frame while needed
          if (key === this._desiredFrame || key === this._drawnFrame) return;
          if (dist > farthest) {
            farthest = dist;
            evictKey = key;
          }
        });
        if (evictKey == null) {
          // fallback: evict oldest insertion
          evictKey = this._cache.keys().next().value;
        }
        if (evictKey == null) break;
        const old = this._cache.get(evictKey);
        this._cache.delete(evictKey);
        this._closeBitmap(old);
      }
    }

    _closeBitmap(value) {
      if (value && typeof value.close === 'function') {
        try {
          value.close();
        } catch (e) {
          /* ignore */
        }
      }
    }

    _releaseCache() {
      this._cache.forEach((value) => this._closeBitmap(value));
      this._cache.clear();
      this._queue = [];
      this._loading.clear();
    }

    _nearestLoaded(index) {
      if (this._cache.has(index)) return index;
      let best = null;
      let bestDist = Infinity;
      this._cache.forEach((_v, key) => {
        const d = Math.abs(key - index);
        if (d < bestDist) {
          bestDist = d;
          best = key;
        }
      });
      return best;
    }

    _draw() {
      if (!this.ctx || !this.canvas) return;
      if (this._staticMode) {
        // Poster handles static display
        return;
      }

      const nearest = this._nearestLoaded(this._desiredFrame);
      if (nearest == null) return;
      if (nearest === this._drawnFrame && this._ready) return;

      const bitmap = this._cache.get(nearest);
      if (!bitmap) return;

      const cw = this.canvas.width;
      const ch = this.canvas.height;
      this.ctx.clearRect(0, 0, cw, ch);

      // Contain within canvas preserving aspect
      const scale = Math.min(cw / SOURCE_W, ch / SOURCE_H);
      const dw = SOURCE_W * scale;
      const dh = SOURCE_H * scale;
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;
      this.ctx.drawImage(bitmap, dx, dy, dw, dh);

      this._drawnFrame = nearest;
      if (!this._ready) {
        this._ready = true;
        this.classList.add('is-ready');
      }
    }

    _ensureFrame(index) {
      if (!this._cache.has(index) && !this._loading.has(index)) {
        this._queue.unshift(index);
        this._pumpQueue();
      }
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, HylaEstScrollNotch);
  }

  // Theme Editor: ensure upgraded elements after section load
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(TAG) || (event.target.matches?.(TAG) ? event.target : null);
    if (root && root.disconnectedCallback && root.connectedCallback) {
      // Browser upgrades automatically; force reconnect if needed
      if (!root._connected) root.connectedCallback();
    }
  });
})();
